const BASE_URL = import.meta.env.DEV
  ? 'http://64.23.188.15:8000/api/v1'
  : '/api/v1';

const fetchWithNoCache = async (url: string, options: RequestInit = {}) => {
  const timestamp = Date.now();
  const urlWithTimestamp = `${url}${url.includes('?') ? '&' : '?'}_t=${timestamp}`;
  
  const token = localStorage.getItem('token');
  console.log('Current token:', token); // Log the token for debugging
  
  const defaultOptions: RequestInit = {
    headers: {
      // 'Cache-Control': 'no-cache, no-store, must-revalidate',
      // 'Pragma': 'no-cache',
      // 'Expires': '0',
      'Content-Type': 'application/json',
      // 'User-Agent': 'PostmanRuntime/7.29.0',
      ...(token && token !== 'undefined' ? { 'Authorization': `Bearer ${token.replace(/"/g, '')}` } : {}),
    },
    cache: 'no-store',
    credentials: 'omit',
    mode: 'cors',
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  console.log('Fetching URL:', urlWithTimestamp);
  console.log('With options:', JSON.stringify(mergedOptions, null, 2));

  try {
    const response = await fetch(urlWithTimestamp, mergedOptions);
    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));
    
    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

export interface BusinessSignupData {
  name: string;
  address: string;
  currency: string;
  signCurrency: string;
  lang: string;
  country: string;
  category: string;
}

export interface UserSignupData {
  username: string;
  password: string;
  phone: string;
  role: 'Admin';
  companyId: string;
  email: string;
}

export interface Category {
  _id: string;
  name: string;
  nameEnglish: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Company {
  _id: string;
  name: string;
  address: string;
  currency: string;
  signCurrency: string;
  lang: string;
  country: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  _id: string;
  name: string;
  code: string;
  description?: string;
  purchasePrice: number;
  salePrice: number;
  quantity: number;
  companyId: Company;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  status: number;
}

export interface LoginResponse {
  message: string;
  data: {
    _id: string;
    username: string;
    phone: string;
    role: string;
    companyId: {
      _id: string;
      name: string;
      address: string;
      currency: string;
      signCurrency: string;
      lang: string;
      country: string;
      category: {
        _id: string;
        name: string;
        nameEnglish: string;
      };
      createdAt: string;
      updatedAt: string;
    };
    createdAt: string;
    updatedAt: string;
  };
  token: string;
}

export interface CreateProductDto {
  name: string;
  description?: string;
  purchasePrice: number;
  salePrice: number;
  quantity: number;
  code: string;
  companyId: string;
}

export interface UpdateProductDto extends CreateProductDto {
  _id: string;
}

export interface SaleProduct {
  productId: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface CreateSaleDto {
  companyId: string;
  products: SaleProduct[];
  totalAmount: number;
  userId: string;
  paymentMode: string;
  status: string;
  discount: number;
}

export interface Sale {
  _id: string;
  companyId: {
    _id: string;
    name: string;
    address: string;
    currency: string;
    signCurrency: string;
    lang: string;
    country: string;
    category: string;
    createdAt: string;
    updatedAt: string;
  };
  products: Array<{
    productId: {
      _id: string;
      name: string;
      code: string;
      purchasePrice: number;
      salePrice: number;
      quantity: number;
      companyId: string;
      createdAt: string;
      updatedAt: string;
      description?: string;
    } | null;
    quantity: number;
    unitPrice: number;
    total: number;
    _id: string;
  }>;
  totalAmount: number;
  userId: {
    _id: string;
    username: string;
    phone: string;
    role: string;
    companyId: string;
    createdAt: string;
    updatedAt: string;
  };
  paymentMode: string;
  status: string;
  discount: number;
  createdAt: string;
  updatedAt: string;
}

export const api = {
  async getCategories(): Promise<Category[]> {
    try {
      const response = await fetchWithNoCache(`${BASE_URL}/categories`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      
      const data = await response.json();
      
      if (!data.categories || !Array.isArray(data.categories)) {
        console.error('Invalid data format:', data);
        throw new Error('Invalid data format received from server');
      }
      
      return data.categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async signupBusiness(data: BusinessSignupData) {
    const response = await fetchWithNoCache(`${BASE_URL}/companies`, {
      method: 'POST',
      body: JSON.stringify(data, null, 2),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('API Error:', JSON.stringify(errorData, null, 2));
      throw new Error(errorData.message || 'Failed to create business');
    }

    const result = await response.json();
    console.log('API Response:', JSON.stringify(result, null, 2));
    return result;
  },

  async createUser(data: UserSignupData) {
    console.log('Data being sent to API:', JSON.stringify(data, null, 2));
    const response = await fetchWithNoCache(`${BASE_URL}/signup`, {
      method: 'POST',
      body: JSON.stringify(data, null, 2),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('API Error:', JSON.stringify(errorData, null, 2));
      throw new Error(errorData.message || 'Failed to create user');
    }

    const result = await response.json();
    console.log('API Response:', JSON.stringify(result, null, 2));
    return result;
  },

  async login(phone: string, password: string): Promise<LoginResponse> {
    console.log('Login attempt for:', { phone, password: '***' });
    const response = await fetchWithNoCache(`${BASE_URL}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ phone, password }, null, 2),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Login Error:', errorData);
      throw new Error('Invalid credentials');
    }

    const result = await response.json();
    console.log('Login Success:', result);
    return result;
  },

  getProductsByCompany: async (companyId: string): Promise<Product[]> => {
    try {
      console.log('Fetching products for company:', companyId);
      
      const response = await fetchWithNoCache(`${BASE_URL}/products/company/${companyId}`);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Products Error:', errorData);
        throw new Error(errorData?.message || `Failed to fetch products: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Raw API Response:', {
        result,
        type: typeof result,
        keys: Object.keys(result),
        isArray: Array.isArray(result),
        hasData: 'data' in result,
        dataType: result.data ? typeof result.data : 'undefined',
        isDataArray: result.data ? Array.isArray(result.data) : false
      });
      
      // Vérifier si le résultat est directement un tableau
      if (Array.isArray(result)) {
        console.log('Result is directly an array');
        return result;
      }
      
      // Vérifier si les données sont dans result.data
      if (result.data && Array.isArray(result.data)) {
        console.log('Data found in result.data');
        return result.data;
      }
      
      console.error('Invalid response format:', result);
      throw new Error('Invalid response format from server');
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  getProducts: async (companyId: string): Promise<Product[]> => {
    try {
      const response = await fetch(`${BASE_URL}/products?companyId=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch products: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  createProduct: async (product: CreateProductDto): Promise<Product> => {
    try {
      const response = await fetch(`${BASE_URL}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(product),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to create product: ${response.statusText}`);
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  updateProduct: async (id: string, product: CreateProductDto): Promise<Product> => {
    try {
      const response = await fetch(`${BASE_URL}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(product),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to update product: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  deleteProduct: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${BASE_URL}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to delete product: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  createSale: async (sale: CreateSaleDto): Promise<Sale> => {
    try {
      const response = await fetch(`${BASE_URL}/sales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(sale),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to create sale: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error creating sale:', error);
      throw error;
    }
  },

  getSales: async (companyId: string): Promise<Sale[]> => {
    try {
      const response = await fetch(`${BASE_URL}/sales?companyId=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch sales: ${response.statusText}`);
      }

      const result = await response.json();
      return result.sales;
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw error;
    }
  },
};
